/**
 * Content Vault hub — Project Harmony · Patch 21
 *
 * Renders the workspace's approved creative library as a first-class
 * destination. Patch 21 wires the page to the real vault endpoints:
 *   • /vault/rollup  → status counts + lazy idempotent backfill.
 *   • /vault/assets  → recently registered vault items.
 *
 * The page still hosts links to /library, /media, /history so power
 * users can drill into the identity spine, but the vault itself now
 * proves — on load — that approved assets are actually persisted.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Folder, Images, Video, Layers, Sparkles, CheckCircle2 } from "lucide-react";

import { HarmonyEmpty } from "@/shared/ui/harmony-empty";
import { Card } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { useWorkspaceMilestones } from "@/entities/workspace/milestones";
import { useCurrentWorkspace } from "@/entities/workspace";
import { useCampaignRunSummary } from "@/entities/campaign-run";
import { PageHeader } from "@/shared/ui/page-header";
import { WorkflowStages } from "@/widgets/WorkflowStages/WorkflowStages";
import { http } from "@/shared/api/http";

const SECTIONS = [
  { to: "/library?collection=written-content", label: "Posts, blogs, emails, landing pages", icon: Layers },
  { to: "/media",    label: "Images, videos, voice, music",        icon: Images },
  { to: "/history",  label: "Version history & regeneration",      icon: Video  },
] as const;

interface VaultAssetCard {
  id: string;
  permanent_asset_id: string | null;
  campaign_pack_id: string | null;
  campaign: string | null;
  objective: string | null;
  content_type: string | null;
  status: string;
  preview: string | null;
  vault_registered_at: string | null;
}

async function fetchVaultAssets(): Promise<{ assets: VaultAssetCard[] }> {
  // Patch 34.2.1d · Errors are propagated to React Query so the caller
  // can distinguish "empty vault" from "vault unreachable". The old
  // `try/catch → { assets: [] }` swallow turned every 401/403/500/network
  // failure into a fake empty vault + a false "Create your first
  // campaign" empty state.
  return http<{ assets: VaultAssetCard[] }>("/vault/assets?limit=12");
}

function humanizeContentType(ct: string | null): string {
  if (!ct) return "Asset";
  return ct.replace(/^social_/, "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function shortDate(iso: string | null): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function VaultHubPage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? null;
  const m = useWorkspaceMilestones(workspaceId);
  // Patch 34.2.1d · Vault assets query is now workspace-scoped and
  // gated on `workspaceId` — no more cache leakage across workspace
  // switches. Retry disabled so error states surface immediately.
  const vaultAssets = useQuery({
    queryKey: ["vault-assets", workspaceId ?? "no-workspace", "recent"],
    queryFn: fetchVaultAssets,
    enabled: Boolean(workspaceId),
    refetchInterval: 60_000,
    retry: false,
  });
  // Patch 34.2.1c · Canonical CampaignRun projection is the source of
  // truth for every founder-facing counter. Vault inventory is retained
  // ONLY as the source for actual asset cards + the integrity cross-
  // check below.
  const runSummary = useCampaignRunSummary(workspace?.id ?? null);

  const assets = vaultAssets.data?.assets ?? [];
  const canonical = runSummary.data?.counters;
  const canonicalReady = !!workspace && !runSummary.isLoading;
  const inventoryReady = !vaultAssets.isLoading && !m.isLoading;
  const bothReady = canonicalReady && inventoryReady;

  // Canonical numbers — always used for the four Vault counters and the
  // drafts banner. Fall back to milestones only during the very first
  // paint (before `runSummary` has resolved) so we never flash zeros.
  const canonicalDrafts = canonical?.draft ?? 0;
  const canonicalApproved = canonical?.approved ?? 0;
  const canonicalScheduled = canonical?.scheduled ?? 0;
  const canonicalPublished = canonical?.published ?? 0;
  const canonicalActivity =
    canonicalDrafts +
    canonicalApproved +
    canonicalScheduled +
    canonicalPublished;

  // Vault inventory numbers — used only for the integrity guard below.
  const inventoryApproved = m.approvedCount;
  const inventoryScheduled = m.scheduledCount;
  const inventoryPublished = m.publishedCount;

  // Integrity mismatches only compute AFTER both queries resolve so
  // nothing flashes during initial load.
  const approvedMismatch =
    bothReady && canonicalApproved !== inventoryApproved;
  const scheduledMismatch =
    bothReady && canonicalScheduled !== inventoryScheduled;
  const publishedMismatch =
    bothReady && canonicalPublished !== inventoryPublished;
  const hasMismatch = approvedMismatch || scheduledMismatch || publishedMismatch;

  // Recovery state — surface the projection error explicitly, do NOT
  // silently fall back to milestone numbers when the canonical source
  // is unreachable.
  const summaryError = runSummary.isError;

  // Patch 34.2.1d · Vault inventory unreachable (401/403/500/network) —
  // NEVER masquerade as an empty vault or a "Create your first campaign"
  // state. Render the recovery UI below and let the founder retry.
  const inventoryError = vaultAssets.isError;

  // True empty state — only when canonical AND inventory both loaded
  // WITHOUT error AND both report zero activity. Never show "Create
  // your first campaign" while CampaignRun reports any activity OR
  // while the Vault query is in an error state.
  const isEmpty =
    bothReady &&
    !inventoryError &&
    !summaryError &&
    canonicalActivity === 0 &&
    assets.length === 0 &&
    m.assetCount === 0;

  return (
    <div className="mx-auto max-w-[1120px] px-4 py-8 md:px-8 md:py-12" data-testid="vault-hub">
      <WorkflowStages current="vault" />
      <PageHeader
        eyebrow="Content Vault"
        title="Content Vault"
        subtitle="Every image, video, post, email, blog, flyer, and campaign your Marketing Department creates is organized here for future use."
        action={
          <Button asChild data-testid="vault-hub-new">
            <Link to="/studio">
              <Sparkles className="h-4 w-4 mr-1.5" aria-hidden /> Create Asset
            </Link>
          </Button>
        }
      />

      {/* Patch 34.2.1c · Canonical projection unavailable — recovery
          state. Never falls back to stitched milestone numbers silently. */}
      {summaryError && (
        <div
          data-testid="vault-summary-error"
          className="mb-6 rounded-xl border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm"
        >
          <div className="font-semibold text-red-800">
            Canonical Vault counters unavailable.
          </div>
          <div className="text-xs text-red-900/80 mt-1">
            Couldn&apos;t reach the CampaignRun projection. Cards below are
            still safe to open. Refresh, or check backend health.
          </div>
        </div>
      )}

      {/* Patch 34.2.1c · Integrity Mismatch guardrail. Only renders once
          BOTH the canonical projection AND the Vault inventory have
          resolved — no false positives during load. */}
      {hasMismatch && (
        <div
          data-testid="vault-integrity-mismatch"
          className="mb-6 rounded-xl border border-red-500/60 bg-red-500/10 px-4 py-3 text-sm"
        >
          <div
            className="font-semibold text-red-800"
            data-testid="vault-integrity-mismatch-title"
          >
            Integrity mismatch — Vault inventory disagrees with canonical projection.
          </div>
          <ul className="mt-2 space-y-0.5 text-xs text-red-900/80">
            {approvedMismatch && (
              <li data-testid="vault-integrity-mismatch-approved">
                Approved: canonical <strong>{canonicalApproved}</strong> vs
                inventory <strong>{inventoryApproved}</strong>
              </li>
            )}
            {scheduledMismatch && (
              <li data-testid="vault-integrity-mismatch-scheduled">
                Scheduled: canonical <strong>{canonicalScheduled}</strong> vs
                inventory <strong>{inventoryScheduled}</strong>
              </li>
            )}
            {publishedMismatch && (
              <li data-testid="vault-integrity-mismatch-published">
                Published: canonical <strong>{canonicalPublished}</strong>{" "}
                vs inventory <strong>{inventoryPublished}</strong>
              </li>
            )}
          </ul>
          <p className="mt-2 text-xs text-red-900/80">
            One of the two is stale. Refresh, or check reconciliation.
          </p>
        </div>
      )}

      {/* Drafts banner — canonical count only. Never flashes: we require
          `canonicalReady` before rendering the banner. */}
      {canonicalReady && canonicalDrafts > 0 && (
        <div
          data-testid="vault-drafts-banner"
          className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--md-amber,#e5b454)]/40 bg-[var(--md-amber,#e5b454)]/10 px-4 py-3"
        >
          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="grid h-8 w-8 place-items-center rounded-lg bg-[var(--md-amber,#e5b454)]/25 text-[var(--md-amber,#e5b454)]"
            >
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="text-sm">
              <div className="font-semibold" data-testid="vault-drafts-banner-count">
                {canonicalDrafts} draft{canonicalDrafts === 1 ? "" : "s"} awaiting review
              </div>
              <div className="text-[12px] text-[color:var(--md-fog,#a0a4ac)]">
                Approve them from the Review Queue to add them to the Vault.
              </div>
            </div>
          </div>
          <Button asChild size="sm" data-testid="vault-drafts-banner-cta">
            <Link to="/campaigns/review">Open Review Queue</Link>
          </Button>
        </div>
      )}

      {/* Loading skeleton — while EITHER the canonical projection or
          the Vault inventory is still loading. We deliberately never
          render the true empty state until both have resolved. */}
      {!bothReady ? (
        <div
          data-testid="vault-hub-loading"
          className="rounded-xl border border-dashed border-[color:var(--md-line)] p-8 text-center text-sm text-[var(--md-fog)]"
        >
          Loading your Vault…
        </div>
      ) : inventoryError ? (
        /* Patch 34.2.1d · Vault inventory request failed. Do NOT
           masquerade as empty. Give the founder three clear actions. */
        <div
          data-testid="vault-inventory-error"
          className="rounded-xl border border-red-500/50 bg-red-500/10 p-6"
        >
          <div className="font-semibold text-red-800" data-testid="vault-inventory-error-title">
            We couldn&apos;t load your Vault assets.
          </div>
          <p className="mt-2 text-sm text-red-900/80">
            The Vault inventory request failed. Your approved content is
            not lost — the projection above still tracks it. Try again,
            return home, or report the issue.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              data-testid="vault-inventory-error-retry"
              onClick={() => { void vaultAssets.refetch(); }}
            >
              Retry
            </Button>
            <Button asChild size="sm" variant="outline" data-testid="vault-inventory-error-home">
              <Link to="/home">Return Home</Link>
            </Button>
            <Button
              asChild
              size="sm"
              variant="ghost"
              data-testid="vault-inventory-error-report"
            >
              <a href="mailto:support@orkexra.com?subject=Vault%20inventory%20error">
                Report Issue
              </a>
            </Button>
          </div>
        </div>
      ) : isEmpty ? (
        <HarmonyEmpty
          eyebrow="Your creative library will grow here."
          title="Approved images, videos, posts, emails, blogs, and campaign assets will be stored for reuse."
          body="Every asset that flows through your Marketing Department lands here automatically."
          icon={<Folder className="h-5 w-5" aria-hidden />}
          primary={{ label: "Create Your First Campaign", to: "/campaigns/new", testId: "vault-empty-cta" }}
          testId="vault-hub-empty"
        />
      ) : (
        <>
          {/* Vault counters — CANONICAL numbers. The Vault inventory is
              only used for the integrity cross-check above. */}
          <div
            className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4"
            data-testid="vault-counters"
          >
            <VaultCounter label="Drafts" value={canonicalDrafts} tone="amber" testId="vault-counter-drafts" />
            <VaultCounter label="Approved" value={canonicalApproved} tone="emerald" testId="vault-counter-approved" />
            <VaultCounter label="Scheduled" value={canonicalScheduled} tone="sky" testId="vault-counter-scheduled" />
            <VaultCounter label="Published" value={canonicalPublished} tone="indigo" testId="vault-counter-published" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="vault-sections">
            {SECTIONS.map((s) => (
              <Link
                key={s.to}
                to={s.to}
                data-testid={`vault-section-${s.to.slice(1)}`}
                className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/40 p-5 transition hover:border-[var(--md-amber)]/50 hover:bg-[var(--md-ink-1)]"
              >
                <s.icon className="h-5 w-5 text-[var(--md-amber)]" aria-hidden />
                <p className="mt-3 text-sm font-semibold text-[var(--md-ivory-0)]">{s.label}</p>
              </Link>
            ))}
          </div>

          {/* Patch 34.1.14 · next-step CTA using CANONICAL numbers. */}
          {canonicalApproved > 0 && canonicalScheduled === 0 && (
            <div
              data-testid="vault-next-step"
              className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3"
            >
              <div className="text-sm">
                <div className="font-semibold text-[var(--md-ivory-0)]">
                  Next step: schedule your approved assets
                </div>
                <div className="text-[12px] text-[color:var(--md-fog,#a0a4ac)]">
                  {canonicalApproved} approved asset{canonicalApproved === 1 ? "" : "s"} ready to publish.
                </div>
              </div>
              <Button asChild size="sm" data-testid="vault-next-step-cta">
                <Link to="/publishing">Open Publishing →</Link>
              </Button>
            </div>
          )}

          {/* Recent additions — the "proof" panel per Patch 21. Vault
              endpoint remains the source for actual cards. */}
          <section className="mt-8" data-testid="vault-recent">
            <div className="mb-3 flex items-baseline justify-between">
              <h2 className="text-lg font-semibold text-[var(--md-ivory-0)]">
                Recent additions
              </h2>
              <Button asChild variant="ghost" size="sm" data-testid="vault-open-library">
                <Link to="/library?collection=written-content">Open written-content library →</Link>
              </Button>
            </div>
            {vaultAssets.isLoading ? (
              <div
                className="rounded-xl border border-dashed border-[color:var(--md-line)] p-6 text-sm text-[var(--md-fog)]"
                data-testid="vault-recent-loading"
              >
                Loading recently vaulted content…
              </div>
            ) : vaultAssets.isError ? (
              <div
                className="rounded-xl border border-red-500/50 bg-red-500/10 p-6 text-sm"
                data-testid="vault-recent-error"
              >
                <div className="font-semibold text-red-800">
                  Couldn&apos;t load Vault cards.
                </div>
                <div className="text-xs text-red-900/80 mt-1">
                  Canonical counters above still reflect the truth. Refresh to retry.
                </div>
              </div>
            ) : assets.length === 0 ? (
              <div
                className="rounded-xl border border-dashed border-[color:var(--md-line)] p-6 text-sm text-[var(--md-fog)]"
                data-testid="vault-recent-empty"
              >
                No approved assets are in the Vault yet. Approve a campaign pack in{" "}
                <Link to="/campaigns/review" className="text-[var(--md-amber)] underline">Content Review</Link>{" "}
                to see it appear here.
              </div>
            ) : (
              <div
                className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
                data-testid="vault-recent-grid"
              >
                {assets.map((a) => {
                  const openHref = a.permanent_asset_id
                    ? `/assets/${a.permanent_asset_id}`
                    : "/library";
                  return (
                    <div
                      key={a.id}
                      data-testid={`vault-recent-card-${a.id}`}
                      className="group flex flex-col rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/40 p-4 transition hover:border-[var(--md-amber)]/50 hover:bg-[var(--md-ink-1)]"
                    >
                      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--md-fog)]">
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" aria-hidden />
                        <span>{humanizeContentType(a.content_type)}</span>
                        <span className="ml-auto">{shortDate(a.vault_registered_at)}</span>
                      </div>
                      <p className="mt-2 text-sm font-semibold text-[var(--md-ivory-0)] line-clamp-2">
                        {a.campaign || a.objective || a.permanent_asset_id || "Approved asset"}
                      </p>
                      {a.preview ? (
                        <p className="mt-2 text-xs text-[var(--md-fog)] line-clamp-3">{a.preview}</p>
                      ) : null}
                      <div className="mt-4 flex items-center justify-between gap-2">
                        <Button
                          asChild
                          size="sm"
                          className="w-full justify-center"
                          data-testid={`vault-open-asset-${a.id}`}
                        >
                          <Link to={openHref}>
                            Open Asset <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden />
                          </Link>
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {m.hasVaultDepth ? (
            <Card className="mt-6 p-5" data-testid="vault-advanced">
              <p className="text-xs uppercase tracking-widest text-[var(--md-fog)] mb-2">
                Vault Tools · unlocked
              </p>
              <p className="text-sm text-[var(--md-ivory-0)]">
                You have {m.assetCount} assets in the Vault. Collections and Smart Search are now available.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button asChild variant="outline" size="sm">
                  <Link to="/library?view=collections">Collections</Link>
                </Button>
                <Button asChild variant="outline" size="sm">
                  <Link to="/library?view=smart-search">Smart Search</Link>
                </Button>
              </div>
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}


interface VaultCounterProps {
  label: string;
  value: number;
  tone: "amber" | "emerald" | "sky" | "indigo";
  testId: string;
}

function VaultCounter({ label, value, tone, testId }: VaultCounterProps): JSX.Element {
  const toneClass = {
    amber:   "text-[var(--md-amber)]",
    emerald: "text-emerald-400",
    sky:     "text-sky-400",
    indigo:  "text-indigo-400",
  }[tone];
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/40 p-4"
    >
      <p className="text-xs uppercase tracking-widest text-[var(--md-fog)]">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}
